import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASEURL;

/**
 * FACOT Payment Utilities
 */

export class FacotPaymentUtils {
  /**
   * Check payment status for a FACOT application
   * @param {string} applicationId - The application ID
   * @returns {Promise<Object>} Payment status data
   */
  static async checkPaymentStatus(applicationId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/facot-payment/status/${applicationId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error checking payment status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to check payment status'
      };
    }
  }

  /**
   * Get detailed payment information for an application
   * @param {string} applicationId - The application ID
   * @returns {Promise<Object>} Detailed payment data
   */
  static async getPaymentDetails(applicationId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/facot-application/payment-details/${applicationId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching payment details:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch payment details'
      };
    }
  }

  /**
   * Create a payment order for FACOT application
   * @param {string} applicationId - The application ID
   * @param {string} paymentType - 'first' or 'second'
   * @returns {Promise<Object>} Payment order data
   */
  static async createPaymentOrder(applicationId, paymentType) {
    try {
      const response = await axios.post(`${API_BASE_URL}/facot-payment/${paymentType}/${applicationId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error creating payment order:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create payment order'
      };
    }
  }

  /**
   * Generate a short receipt ID for Razorpay (max 40 characters)
   * @param {string} applicationId - The application ID
   * @param {string} paymentType - 'first' or 'second'
   * @returns {string} Short receipt ID
   */
  static generateReceiptId(applicationId, paymentType) {
    // Create a short receipt ID: FACOT + last 8 chars of app ID + payment type + timestamp
    const shortAppId = applicationId.slice(-8);
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    const typeCode = paymentType === 'first' ? 'F' : 'S';
    
    return `FACOT${shortAppId}${typeCode}${timestamp}`.substring(0, 40);
  }

  /**
   * Verify payment with Razorpay
   * @param {Object} paymentResponse - Payment response from Razorpay
   * @param {string} paymentType - 'first' or 'second'
   * @param {string} applicationId - The application ID
   * @returns {Promise<Object>} Verification result
   */
  static async verifyPayment(paymentResponse, paymentType, applicationId) {
    try {
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        paymentType: paymentType,
        applicationId: applicationId,
      };

      const response = await axios.post(`${API_BASE_URL}/facot-payment/verify`, verificationData);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error verifying payment:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Payment verification failed'
      };
    }
  }

  /**
   * Load Razorpay script dynamically
   * @returns {Promise<boolean>} Success status
   */
  static async loadRazorpayScript() {
    return new Promise((resolve) => {
      // Check if script is already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  /**
   * Get payment status text
   * @param {string} status - Payment status
   * @returns {string} Human-readable status
   */
  static getPaymentStatusText(status) {
    const statusMap = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled',
      'refunded': 'Refunded'
    };
    return statusMap[status] || 'Unknown';
  }

  /**
   * Get approval status text
   * @param {string} status - Approval status
   * @returns {string} Human-readable status
   */
  static getApprovalStatusText(status) {
    const statusMap = {
      'pending': 'Pending Secretary Approval',
      'approved': 'Approved by Secretary',
      'rejected': 'Rejected by Secretary'
    };
    return statusMap[status] || 'Unknown';
  }

  /**
   * Get status variant for UI components
   * @param {string} status - Status value
   * @returns {string} Bootstrap variant
   */
  static getStatusVariant(status) {
    const variantMap = {
      'completed': 'success',
      'approved': 'success',
      'pending': 'warning',
      'failed': 'danger',
      'rejected': 'danger',
      'cancelled': 'secondary'
    };
    return variantMap[status] || 'secondary';
  }

  /**
   * Check if first payment is completed and approved
   * @param {Object} paymentStatus - Payment status object
   * @returns {boolean} True if first payment is ready
   */
  static isFirstPaymentReady(paymentStatus) {
    return paymentStatus?.paymentfirst_status === 'completed' && 
           paymentStatus?.firstapproval_status === 'approved';
  }

  /**
   * Check if second payment can be made
   * @param {Object} paymentStatus - Payment status object
   * @returns {boolean} True if second payment is available
   */
  static canMakeSecondPayment(paymentStatus) {
    return this.isFirstPaymentReady(paymentStatus) && 
           paymentStatus?.paymentafter_status !== 'completed';
  }

  /**
   * Check if process is completely finished
   * @param {Object} paymentStatus - Payment status object
   * @returns {boolean} True if process is complete
   */
  static isProcessComplete(paymentStatus) {
    return paymentStatus?.paymentfirst_status === 'completed' &&
           paymentStatus?.firstapproval_status === 'approved' &&
           paymentStatus?.paymentafter_status === 'completed' &&
           paymentStatus?.afterapproval_status === 'approved';
  }

  /**
   * Get current step in the payment process
   * @param {Object} paymentStatus - Payment status object
   * @returns {number} Current step (1, 2, or 3)
   */
  static getCurrentStep(paymentStatus) {
    if (this.isProcessComplete(paymentStatus)) {
      return 3; // Complete
    } else if (this.isFirstPaymentReady(paymentStatus)) {
      return 2; // Second Payment
    } else {
      return 1; // First Payment
    }
  }

  /**
   * Format currency amount
   * @param {number} amount - Amount in paise
   * @returns {string} Formatted currency string
   */
  static formatCurrency(amount) {
    return `₹${(amount / 100).toLocaleString()}`;
  }

  /**
   * Get payment amount for specific payment type
   * @param {string} paymentType - 'first' or 'second'
   * @returns {number} Amount in paise
   */
  static getPaymentAmount(paymentType) {
    const amounts = {
      'first': 354000, // ₹3,540 in paise
      'second': 826000  // ₹8,260 in paise
    };
    return amounts[paymentType] || 0;
  }

  /**
   * Get total FACOT application amount
   * @returns {number} Total amount in paise
   */
  static getTotalAmount() {
    return 1180000; // ₹11,800 in paise
  }

  /**
   * Validate payment response from Razorpay
   * @param {Object} paymentResponse - Payment response object
   * @returns {boolean} True if valid
   */
  static validatePaymentResponse(paymentResponse) {
    return paymentResponse &&
           paymentResponse.razorpay_order_id &&
           paymentResponse.razorpay_payment_id &&
           paymentResponse.razorpay_signature;
  }

  /**
   * Get error message for payment failures
   * @param {Error} error - Error object
   * @returns {string} User-friendly error message
   */
  static getErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Check if application is eligible for payment
   * @param {Object} applicationData - Application data
   * @returns {Object} Eligibility check result
   */
  static checkPaymentEligibility(applicationData) {
    const errors = [];
    
    if (!applicationData) {
      errors.push('Application data is required');
    }
    
    if (!applicationData?.applicantName) {
      errors.push('Applicant name is required');
    }
    
    if (!applicationData?.email) {
      errors.push('Email address is required');
    }
    
    if (!applicationData?.aiotaMembershipNo) {
      errors.push('AIOTA membership number is required');
    }
    
    if (!applicationData?.acotRegistrationNo) {
      errors.push('ACOT registration number is required');
    }
    
    return {
      eligible: errors.length === 0,
      errors: errors
    };
  }
}

export default FacotPaymentUtils;
